<script setup lang="ts">
import { computed } from 'vue'
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/shadcn'
import SelectGroup from '@/components/shadcn/SelectGroup.vue'
import SelectLabel from '@/components/shadcn/SelectLabel.vue'
import SelectItem from '@/components/shadcn/SelectItem.vue'

export interface Option {
  value: string | number
  label: string
}

export interface GroupedOption {
  groupLabel: string
  options: Option[]
}

type StyleType =
  | 'default'   // 기본 스타일
  | 'error'     // 에러 상태
  | 'success'   // 성공 상태
  | 'warning'   // 경고 상태
  | 'sm'        // 작은 크기
  | 'lg'        // 큰 크기

const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    groupedOptions?: GroupedOption[]
    placeholder?: string
    disabled?: boolean
    required?: boolean
    name?: string
    id?: string
    class?: string
    /** 스타일 프리셋 */
    styletype?: StyleType
  }>(),
  {
    groupedOptions: () => [],
    placeholder: '선택하세요',
    disabled: false,
    required: false,
    styletype: 'default',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  'change': [value: string | number]
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

/**
 * styletype -> class 매핑
 * JCombo와 동일한 스타일 프리셋 사용
 */
const STYLE_PRESETS: Record<StyleType, { class: string }> = {
  default: { class: '' },
  error: { 
    class: 'border-destructive focus:ring-destructive',
  },
  success: { 
    class: 'border-green-500 focus:ring-green-500',
  },
  warning: { 
    class: 'border-amber-500 focus:ring-amber-500',
  },
  sm: { 
    class: 'h-8 text-xs px-2',
  },
  lg: { 
    class: 'h-12 text-base px-4',
  },
}

const triggerClass = computed(() => {
  const styleKey = props.styletype || 'default'
  const preset = STYLE_PRESETS[styleKey] ?? STYLE_PRESETS.default
  return [preset?.class, props.class].filter(Boolean).join(' ')
})

const handleChange = (value: string | number) => {
  emit('update:modelValue', value)
  emit('change', value)
}
</script>

<template>
  <Select 
    :model-value="modelValue" 
    :disabled="disabled"
    :required="required"
    :name="name"
    @update:model-value="(value) => handleChange(value as string | number)"
    @focus="emit('focus', $event as FocusEvent)"
    @blur="emit('blur', $event as FocusEvent)"
  >
    <SelectTrigger :id="id" :class="triggerClass">
      <SelectValue :placeholder="placeholder" />
    </SelectTrigger>
    <SelectContent>
      <SelectGroup v-for="group in groupedOptions" :key="group.groupLabel">
        <SelectLabel>{{ group.groupLabel }}</SelectLabel>
        <SelectItem 
          v-for="option in group.options" 
          :key="option.value" 
          :value="String(option.value)"
        >
          {{ option.label }}
        </SelectItem>
      </SelectGroup>
    </SelectContent>
  </Select>
</template>

